import {PersistConfig, createMigrate} from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/es/stateReconciler/autoMergeLevel2';
import storage from 'redux-persist/es/storage';
import {migrations} from './migrations';
// Type-only: keeps the reducer graph (and the whole core barrel with it) out of
// this module's runtime imports, so it stays cheap to import from a test.
import type rootReducer from './reducers';

type PersistedRootState = ReturnType<typeof rootReducer>;

// Kept out of ./index.tsx so it can be imported without building a real store
// and persistor: that module calls configureStore/persistStore at module scope.
//
// Annotated rather than inferred on purpose. redux-persist reads a fixed set of
// properties off this object and ignores anything else without a warning, so a
// misspelt key is dead config that still reads as done — which is how
// `stateReconcilier` (sic) sat here silently defaulting the whole webapp to
// autoMergeLevel1. The annotation turns that class of typo into a tsc error.
export const persistConfig: PersistConfig<PersistedRootState> = {
  key: 'root',
  storage,
  version: 8,
  // `ui` used to be here: it was this slice's name until e1e72b399 (2024-02-01)
  // renamed it to `uiInteraction` and left the blacklist behind, which is how
  // the slice came to be persisted for ~19 months (#190). The webapp has no
  // `ui` reducer of its own, so the old entry was filtering nothing. (Mobile's
  // blacklist does keep a real `ui` slice -- same word, different thing.)
  // `staffElevation` holds the staff second-factor token: a credential, kept
  // in memory only so a reload asks for a fresh code.
  blacklist: ['uiInteraction', 'data', 'weatherData', 'staffElevation'],
  stateReconciler: autoMergeLevel2,
  debug: true,
  migrate: createMigrate(migrations, {debug: true}),
  writeFailHandler: (err: Error) => console.error('[PersistError] ', err),
};
